import { useState } from 'react';
import { chargebackService } from 'commons/services';
import {
  uploadFileToS3,
  useTranslationWithPrefix,
  validateFile,
} from 'commons/utils';
import { logFailure, logFileSize } from 'commons/utils/documentUploader/logger';

interface UseChargebackDocumentUploaderProps {
  documentName: string;
  onSuccess: (key: string, duration: number) => void;
  onFileError: (text: string) => void;
  onUploadError: () => void;
  onDelete: () => void;
  maxDocumentSize: number;
  accept: string;
  orderId: string;
}

export function useChargebackDocumentUploader({
  documentName,
  onSuccess,
  onFileError,
  onUploadError,
  onDelete,
  maxDocumentSize,
  accept,
  orderId,
}: UseChargebackDocumentUploaderProps) {
  const { t } = useTranslationWithPrefix('chargebackUpload');

  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState('');

  const handleFileChange = async (file: File) => {
    setIsLoading(true);
    const startTime = performance.now();

    const validation = validateFile(file, maxDocumentSize, accept, {
      invalidType: t('errors.invalidType'),
      sizeLimit: t('errors.sizeLimit'),
    });
    if (!validation.isValid) {
      onFileError(validation.errorMessage!);
      setIsLoading(false);
      return;
    }

    logFileSize(file, documentName, 'ChargebackDocumentUploader.tsx');
    onFileError('');
    setFileName(file.name);

    try {
      const uploadUrl = await chargebackService.generateChargebackS3UploadUrl(
        orderId,
        file.type,
      );
      await uploadFileToS3(file, uploadUrl);
      const newBlobUrl = URL.createObjectURL(file);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(newBlobUrl);

      onSuccess(uploadUrl.fields.key, performance.now() - startTime);
    } catch (error) {
      logFailure(file, documentName, error);
      onUploadError();
      setFileName(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileDelete = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl('');
    setFileName(null);
    onDelete();
  };

  return {
    isLoading,
    fileName,
    blobUrl,
    handleFileChange,
    handleFileDelete,
  };
}
